import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Draggable } from '@hello-pangea/dnd'
import { toggleCompleted, deleteAction } from '../actions'
import { get, isEmpty } from 'lodash'
import { todaySelector } from '../actions/selectors'
import {
  getTheme,
  getDisplayDate,
  getDisplayCompletionDate,
  getActionsRepeatText,
  isCompleted,
  actionsAreEqual,
} from '../utils'
import { getActionHref } from '../utils/href'
import ActionCard from './ui/ActionCard'
import { trackEvent } from '../analytics'
import { getSubtaskCount } from '../utils/subtasks'
import { getActionReminderTime, getHasReminder } from '../utils/reminders'
import { getActionDeadlineText } from '../utils/deadline'

const getListId = (action) => get(action, 'listData.themeId', null)

class ActionInner extends Component {
  static propTypes = {
    action: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    inSchedule: PropTypes.bool,
    inLogbook: PropTypes.bool,
    index: PropTypes.number,
    /** When shown in a list column */
    listId: PropTypes.string,
    /** When shown on the schedule (YYYYMMDDetc.) */
    scheduleDay: PropTypes.string,
  }

  state = {
    justCompleted: null,
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.index !== nextProps.index) {
      // List reordering
      return true
    }
    if (this.props.listId !== nextProps.listId) {
      return true
    }
    if (this.props.scheduleDay !== nextProps.scheduleDay) {
      return true
    }
    if (this.props.today !== nextProps.today) {
      // it's a new day
      return true
    }
    if (this.props.location.actionId !== nextProps.location.actionId) {
      // clicked a different Action
      return true
    }
    if (
      this.props.location.logbookVisible !== nextProps.location.logbookVisible
    ) {
      // changed to logbook
      return true
    }
    if (this.state.justCompleted !== nextState.justCompleted) {
      // just completed
      return true
    }

    const listIdBefore = getListId(this.props.action)
    const listIdAfter = getListId(nextProps.action)

    if (listIdBefore !== listIdAfter) {
      // changed list
      return true
    }

    if (this.props.darkMode !== nextProps.darkMode) {
      // going dark
      if (listIdAfter == null) {
        // only need to re-render Actions in default list (null list ID)
        return true
      }
    }

    return !actionsAreEqual(this.props.action, nextProps.action)
  }

  toggleComplete = () => {
    // Trigger action
    const { action, toggleCompleted } = this.props
    const completed = isCompleted(action)

    this.setState({ justCompleted: true })

    setTimeout(() => {
      toggleCompleted({
        actionId: action.identifier,
        isCompleted: completed,
      })
    }, 300)

    if (completed) trackEvent('Action Uncompleted')
    else trackEvent('Action Completed')
  }

  clickDelete = () => {
    // Trigger action
    const { action, deleteAction } = this.props
    deleteAction({
      actionId: action.identifier,
    })
  }

  getDisplayDate = () => {
    const { action, inSchedule = false } = this.props
    return getDisplayDate(action, inSchedule)
  }

  getDisplayCompletionDate = () => {
    const date = getDisplayCompletionDate(this.props.action)
    if (date == null) return null
    return `Completed ${date}`
  }

  getListName = () => {
    const { action, inSchedule = false, inLogbook = false } = this.props
    const showListName =
      (inSchedule || inLogbook) && action.listData && !action.listData.isHidden

    return showListName ? action.listData.title : null
  }

  getDisplayRepeats = () => {
    return getActionsRepeatText(this.props.action, true)
  }

  getDisplayDeadline = () => {
    return getActionDeadlineText(this.props.action, false)
  }

  getDisplayReminderTime = () => {
    return getActionReminderTime(this.props.action)
  }

  getSubtaskCount = () => {
    const { action } = this.props
    // if we have subtasks locally then count those rather than relying on serverside counter
    if (!isEmpty(action.subtasks)) return getSubtaskCount(action.subtasks)

    return {
      completed: get(action, 'subtasksCompleted', 0),
      total: get(action, 'subtasksTotal', 0),
    }
  }

  render() {
    const {
      action,
      index,
      inSchedule = false,
      inLogbook = false,
      darkMode,
      location,
    } = this.props
    const { actionId: routeActionId, logbookVisible } = location
    const theme = getTheme(get(action, 'listData.themeId'), darkMode)
    const { total, completed } = this.getSubtaskCount()

    const actionId = action.identifier

    // Ensure draggable ID is unique by including inSchedule
    const draggableId = JSON.stringify({ action: actionId, inSchedule })

    // Flag whether a reminder is available on this action
    const hasReminder = getHasReminder(action)

    const actionPriority = action.priority == null ? 0 : action.priority

    if (inLogbook) {
      // Action is in Logbook pane - no drag and drop yet
      return (
        <ActionCard
          bg={theme.bg}
          color={theme.text}
          title={action.title}
          listName={this.getListName()}
          displayDate={this.getDisplayCompletionDate()}
          displayRepeat={this.getDisplayRepeats()}
          displayDeadline={this.getDisplayDeadline()}
          priority={actionPriority}
          subtasksTotal={total}
          subtasksCompleted={completed}
          href={getActionHref(action, true)}
          isCompleted={isCompleted(action) && !this.state.justCompleted}
          hasReminder={hasReminder}
          displayReminderTime={this.getDisplayReminderTime()}
          // funcs
          toggleComplete={this.toggleComplete}
          clickDelete={this.clickDelete}
        />
      )
    } else {
      // Action is in schedule or list
      const isOpen = actionId === routeActionId
      return (
        <Draggable draggableId={draggableId} index={index}>
          {(provided, snapshot) => (
            <ActionCard
              bg={theme.bg}
              color={theme.text}
              title={action.title}
              listName={this.getListName()}
              displayDate={this.getDisplayDate()}
              displayRepeat={this.getDisplayRepeats()}
              displayDeadline={this.getDisplayDeadline()}
              priority={actionPriority}
              subtasksTotal={total}
              subtasksCompleted={completed}
              href={getActionHref(action, logbookVisible)}
              index={index}
              scheduleDisplayIndex={
                process.env.NODE_ENV === 'development'
                  ? (get(action, 'myData.scheduleDisplayIndex') ??
                    get(action, 'scheduleDisplayIndex'))
                  : undefined
              }
              createdAt={
                process.env.NODE_ENV === 'development'
                  ? get(action, 'createdAt')
                  : undefined
              }
              isOpen={isOpen}
              isDragging={snapshot.isDragging && !snapshot.isDropAnimating}
              inSchedule={inSchedule}
              isCompleted={isCompleted(action) || this.state.justCompleted}
              hasReminder={hasReminder}
              displayReminderTime={this.getDisplayReminderTime()}
              // dnd
              ref={provided.innerRef}
              draggableProps={provided.draggableProps}
              dragHandleProps={provided.dragHandleProps}
              // funcs
              toggleComplete={this.toggleComplete}
              clickDelete={this.clickDelete}
            />
          )}
        </Draggable>
      )
    }
  }
}

const mapStateToProps = (state) => ({
  today: todaySelector(state),
  darkMode: get(state, 'preferences.actions.darkMode', true),
  location: state.location,
})

const Action = connect(mapStateToProps, { toggleCompleted, deleteAction })(
  ActionInner,
)
export default Action
